import { LinearGradient } from 'expo-linear-gradient';
import { Pressable, StyleSheet, View, type ViewStyle } from 'react-native';
import { colors } from '../../theme/colors';
import { darkPrecisionFontFamilies } from '../../theme/typography';
import { Text } from './Text';

interface HeaderAvatarProps {
  initials: string;
  size?: number;
  onPress?: () => void;
  accessibilityLabel?: string;
  style?: ViewStyle;
}

export function HeaderAvatar({
  initials,
  size = 32,
  onPress,
  accessibilityLabel = 'Account menu',
  style,
}: Readonly<HeaderAvatarProps>) {
  const ringSize = size + 4;
  const Content = (
    <LinearGradient
      colors={
        [colors.primary.neon, colors.primary.main, colors.secondary] as readonly [
          string,
          string,
          string,
        ]
      }
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={[
        styles.ring,
        { width: ringSize, height: ringSize, borderRadius: ringSize / 2 },
        style,
      ]}
    >
      <View style={[styles.inner, { width: size, height: size, borderRadius: size / 2 }]}>
        <Text
          style={[
            styles.initials,
            {
              fontSize: size * 0.4,
              lineHeight: size * 0.48,
              fontFamily: darkPrecisionFontFamilies.display,
            },
          ]}
          numberOfLines={1}
        >
          {initials.slice(0, 2).toUpperCase()}
        </Text>
      </View>
    </LinearGradient>
  );

  if (!onPress) return Content;

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      hitSlop={8}
      style={({ pressed }) => [pressed && styles.pressed]}
    >
      {Content}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  ring: {
    padding: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  inner: {
    backgroundColor: colors.background.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  initials: {
    color: colors.text.primary,
    fontWeight: '500',
    letterSpacing: 0.4,
  },
  pressed: {
    opacity: 0.85,
  },
});
